/**
 * The source identity + ETag the Move dialog shows and sends (S8 Task 8.10, finding S8-02). A Data Lake row supplies
 * its LISTED ETag — only because 8.0.3 verified that listed, HEAD and ACL ETags are the same value (NOTES row A6,
 * `listedEtagEqualsHeadEtag`); a Blob-view row carries none (a Blob-view folder is a prefix), so the dialog resolves the
 * path through `hns-resolve-path` when it opens. A resolve failure (gone, 403) or a path whose kind changed since it was
 * shown is an error, and Submit stays disabled until a source ETag exists. `reload()` always resolves afresh.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { hnsServiceFacts } from '../../../../core/engines/azureStorageHnsFacts';
import type { HnsPathRef, StorageOperationError } from '../../../../types/azureStorage';
import { cancelStorageRequest, newStorageRequestId, toStorageOperationError } from '../services/azureStorageApi';
import { hnsResolvePath } from './hnsApi';

export type MoveSource =
  | { state: 'resolving' }
  | { state: 'ready'; resource: HnsPathRef; etag: string; via: 'listing' | 'resolve' }
  | { state: 'error'; error: StorageOperationError };

const kindWord = (ref: HnsPathRef): string => (ref.isDirectory ? 'folder' : 'file'); // F7: the UI's word for a Data Lake directory

export function useMoveSource(args: { connectionId: string; resource: HnsPathRef; listedEtag?: string; tabId?: string }) {
  const { connectionId, resource, listedEtag, tabId } = args;
  const fromListing = !!listedEtag && hnsServiceFacts().listedEtagEqualsHeadEtag;
  const [source, setSource] = useState<MoveSource>(() => (fromListing ? { state: 'ready', resource, etag: listedEtag!, via: 'listing' } : { state: 'resolving' }));
  const [attempt, setAttempt] = useState(fromListing ? 0 : 1);
  const resourceRef = useRef(resource);
  resourceRef.current = resource;

  useEffect(() => {
    if (attempt === 0) return;
    const expected = resourceRef.current;
    const requestId = newStorageRequestId();
    let live = true;
    let settled = false;
    setSource({ state: 'resolving' });
    hnsResolvePath({ connectionId, target: { fileSystem: expected.fileSystem, path: expected.path }, requestId }, { tabId })
      .finally(() => { settled = true; })
      .then((answer) => {
        if (!live) return;
        if (answer.ok === false) { setSource({ state: 'error', error: answer.error }); return; }
        if (answer.value.resource.isDirectory !== expected.isDirectory) {
          setSource({ state: 'error', error: { category: 'conflict', code: 'SourceChanged', retryable: false, message: `${expected.fileSystem}/${expected.path} is now a ${kindWord(answer.value.resource)}; refresh the list.` } });
          return;
        }
        setSource({ state: 'ready', resource: answer.value.resource, etag: answer.value.etag, via: 'resolve' });
      })
      .catch((error: unknown) => { if (live) setSource({ state: 'error', error: toStorageOperationError(error) }); });
    return () => { live = false; if (!settled) void cancelStorageRequest(requestId); };
  }, [attempt, connectionId, tabId]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);
  return { source, reload };
}

/**
 * The dialog's source line (UI/UX fix round A, review U37): "Renaming file orders.csv in lake/raw/2026" — what is renamed
 * or moved and where it is, then "checking…" or the refusal while the source is not ready. (It used to end in "as
 * listed" / "checked just now"; the `data-via` attribute keeps that distinction for tests.)
 */
export function moveSourceLine(resource: HnsPathRef, source: MoveSource, action: 'rename' | 'move' = 'move'): string {
  const slash = resource.path.lastIndexOf('/');
  const name = resource.path.slice(slash + 1) || resource.fileSystem;
  const parent = slash < 0 ? resource.fileSystem : `${resource.fileSystem}/${resource.path.slice(0, slash)}`;
  const head = `${action === 'rename' ? 'Renaming' : 'Moving'} ${resource.isDirectory ? 'folder' : 'file'} ${name} in ${parent}`;
  if (source.state === 'resolving') return `${head} — checking…`;
  if (source.state === 'error') return `${head} — ${source.error.message}`;
  return head;
}
